/**
 * ComposedMechanicRenderer.js — Universal Renderer
 *
 * Orchestrates rendering of a composed mechanic JSON. Picks the correct
 * container (AbilityContainer or DomainCardContainer), renders action
 * buttons, token displays, and input collectors. Handles execution
 * results from ActionExecutor including character updates, toasts,
 * session state, deferred actions, ally broadcasting, and auto-clear.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #176)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { executeAction } from './ActionExecutor';
import { executeEffect } from './effects';
import ComposedActionButton from './ComposedActionButton';
import ComposedTokenDisplay from './ComposedTokenDisplay';
import ComposedInputCollector from './ComposedInputCollector';
import AbilityContainer from '../components/character-sheet/AbilityContainer';
import { DomainCardContainer } from '../components/domain-cards/ui';
import ActiveStateIndicator from '../components/domain-cards/ui/ActiveStateIndicator';
import {
  broadcastTargetedHealing,
  broadcastTargetedStressClear,
  broadcastTargetedHope,
  broadcastTargetedArmorBuff,
} from '../services/PartyBroadcastService';
import { useCampaign } from '../context/CampaignContext';

/**
 * @param {object} props
 * @param {object} props.mechanic - Composed Mechanic JSON
 * @param {object} props.character - Character object
 * @param {function} props.updateCharacter - State updater
 * @param {string} [props.cardKey] - Key for character.cardResources lookup
 * @param {object} [props.sessionState={}] - External session state
 * @param {function} [props.setSessionState] - Session state updater
 * @param {function} [props.setPendingSpellAttack] - DiceRoller integration
 * @param {function} [props.setParentActiveTab] - Tab switch handler
 * @param {object} [props.allData] - Game data context
 * @param {boolean} [props.disabled=false] - External disable
 * @param {string} [props.disabledReason=''] - External disable reason
 * @param {string} [props.containerType] - Override AbilityContainer type
 */
const ComposedMechanicRenderer = ({
  mechanic,
  character,
  updateCharacter,
  cardKey,
  sessionState = {},
  setSessionState,
  setPendingSpellAttack,
  setParentActiveTab,
  allData,
  disabled = false,
  disabledReason = '',
  containerType,
  ability,
}) => {
  // ── Stale closure prevention ──
  const characterRef = useRef(character);
  useEffect(() => { characterRef.current = character; }, [character]);

  // ── Local state ──
  const [cooldowns, setCooldowns] = useState({});
  const [pendingInput, setPendingInput] = useState(null);
  const [pendingActionId, setPendingActionId] = useState(null);

  // ── Auto-clear timer ──
  const timeoutRef = useRef(null);
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // ── Campaign context for ally broadcasting ──
  let campaignId = null;
  try {
    const campaignContext = useCampaign();
    campaignId = campaignContext?.campaignId;
  } catch {
    // Rendered outside CampaignProvider (e.g., Workshop preview)
  }

  // ── Ally broadcast handler ──
  const broadcastAllyUpdates = useCallback(async (allyUpdates) => {
    if (!allyUpdates?.length || !campaignId) return;

    const source = {
      characterId: character?.id,
      name: character?.name,
      abilityName: mechanic.meta.name,
    };

    for (const update of allyUpdates) {
      if (!update.targetAllyId) continue;

      switch (update.type) {
        case 'heal':
          await broadcastTargetedHealing(campaignId, update.targetAllyId, update.amount, source);
          break;
        case 'clear_stress':
          await broadcastTargetedStressClear(campaignId, update.targetAllyId, update.amount, source);
          break;
        case 'grant_hope':
          await broadcastTargetedHope(campaignId, update.targetAllyId, update.amount, source);
          break;
        case 'grant_armor':
          await broadcastTargetedArmorBuff(
            campaignId,
            update.targetAllyId,
            { amount: update.amount, expiresOn: update.expiresOn || 'rest' },
            source
          );
          break;
        default:
          break;
      }
    }
  }, [campaignId, character?.id, character?.name, mechanic.meta.name]);

  // ── Core execution handler ──
  const handleExecuteAction = useCallback(async (action, userInput = null) => {
    // ── Toggle-off path: skip ActionExecutor, apply toggleOff effects directly ──
    if (userInput?.isToggleOff && action.toggleOff) {
      const updated = { ...characterRef.current };
      const toasts = [];
      const ctx = { character: updated, cardKey };
      for (const effect of (action.toggleOff.effects || [])) {
        const effectResult = executeEffect(effect, ctx);
        if (effectResult.toasts?.length) toasts.push(...effectResult.toasts);
      }
      await updateCharacter(updated);
      toasts.forEach(t => {
        toast.success(t.message, { icon: t.icon, duration: t.duration });
      });
      return;
    }

    const context = {
      character: characterRef.current,
      cardKey,
      sessionState: sessionState || {},
      cooldowns,
    };

    const result = executeAction(action, mechanic, context, userInput);

    switch (result.status) {
      case 'failed':
        (result.toasts || []).forEach(t => {
          if (t.type === 'error') toast.error(t.message, { duration: t.duration });
          else toast(t.message, { icon: t.icon, duration: t.duration });
        });
        break;

      case 'awaiting_input':
        setPendingInput(result.inputSpec);
        setPendingActionId(action.id);
        break;

      case 'completed':
      case 'deferred': {
        // 1. Apply character mutations
        if (result.character) {
          await updateCharacter(result.character);
        }

        // 2. Apply session state updates
        if (result.sessionStateUpdates && Object.keys(result.sessionStateUpdates).length > 0 && setSessionState) {
          setSessionState(prev => ({ ...prev, ...result.sessionStateUpdates }));
        }

        // 3. Apply cooldown updates
        if (result.cooldowns && Object.keys(result.cooldowns).length > 0) {
          setCooldowns(prev => ({ ...prev, ...result.cooldowns }));
        }

        // 4. Show toasts
        (result.toasts || []).forEach(t => {
          if (t.type === 'error') toast.error(t.message, { duration: t.duration });
          else toast.success(t.message, { icon: t.icon, duration: t.duration });
        });

        // 5. Handle deferred action (route to DiceRoller)
        if (result.deferredAction && setPendingSpellAttack) {
          setPendingSpellAttack(result.deferredAction);
        }

        // 6. Handle tab switch
        if (result.tabSwitch && setParentActiveTab) {
          setParentActiveTab(result.tabSwitch);
        }

        // 7. Broadcast ally updates
        if (result.allyUpdates?.length > 0) {
          broadcastAllyUpdates(result.allyUpdates);
        }

        // 8. Auto-clear timer
        if (result.autoClear) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            // Clear session state keys that were set
            if (result.sessionStateUpdates && setSessionState) {
              const clearUpdates = {};
              Object.keys(result.sessionStateUpdates).forEach(key => {
                clearUpdates[key] = null;
              });
              setSessionState(prev => ({ ...prev, ...clearUpdates }));
            }
            toast(`${mechanic.meta.name} effect faded`, { icon: '✨', duration: 2000 });
          }, result.autoClear);
        }

        break;
      }

      default:
        break;
    }
  }, [
    mechanic, cardKey, sessionState, cooldowns, updateCharacter,
    setSessionState, setPendingSpellAttack, setParentActiveTab,
    broadcastAllyUpdates,
  ]);

  // ── Input re-execution handler ──
  const handleInputSubmit = useCallback((userInput) => {
    const action = mechanic.actions?.find(a => a.id === pendingActionId);
    if (action) {
      handleExecuteAction(action, userInput);
    }
    setPendingInput(null);
    setPendingActionId(null);
  }, [pendingActionId, mechanic, handleExecuteAction]);

  const handleInputCancel = useCallback(() => {
    setPendingInput(null);
    setPendingActionId(null);
  }, []);

  // ── Detect active state for indicator ──
  const activeStateKey = mechanic.actions
    ?.flatMap(a => a.effects || [])
    .find(e => e.type === 'set_session_state')?.key;
  const activeCharFlag = mechanic.actions
    ?.flatMap(a => a.effects || [])
    .find(e => e.type === 'set_character_flag')?.flag;
  const isActive = (activeStateKey ? !!sessionState[activeStateKey] : false)
    || (activeCharFlag ? !!character[activeCharFlag] : false);

  // ── Render helpers ──
  const { meta, tokens, actions, display } = mechanic;

  const actionButtonElements = (actions || []).map(action => (
    <ComposedActionButton
      key={action.id}
      action={action}
      character={character}
      cardKey={cardKey}
      sessionState={sessionState}
      cooldowns={cooldowns}
      disabled={disabled}
      disabledReason={disabledReason}
      onExecute={handleExecuteAction}
    />
  ));

  const renderTokenDisplays = (style) => {
    if (!tokens?.length) return null;
    const displayStyle = display?.counterDisplay?.style || style || 'dots';
    return (
      <div className="flex flex-wrap gap-2">
        {tokens.map(token => (
          <ComposedTokenDisplay
            key={token.id}
            tokenDef={token}
            character={character}
            cardKey={cardKey}
            style={displayStyle}
          />
        ))}
      </div>
    );
  };

  const renderInputCollector = () => {
    if (!pendingInput) return null;
    return (
      <ComposedInputCollector
        inputSpec={pendingInput}
        onSubmit={handleInputSubmit}
        onCancel={handleInputCancel}
      />
    );
  };

  const renderActiveIndicator = () => {
    if (!isActive || meta.hideActiveIndicator) return null;
    return (
      <ActiveStateIndicator
        message={`${meta.name} is active`}
        variant="purple"
        icon={meta.icon}
      />
    );
  };

  // ── Container selection ──
  if (meta.containerContext === 'ability') {
    const effectiveType = containerType || meta.containerType || 'class';
    return (
      <AbilityContainer
        name={meta.name}
        text={ability?.text || meta.description || ''}
        summary={meta.summary}
        type={effectiveType}
        subtitle={meta.subtitle}
        statusContent={
          <>
            {renderTokenDisplays('dots')}
            {renderActiveIndicator()}
            {renderInputCollector()}
          </>
        }
      >
        {actions?.length > 0 && (
          <div className="flex flex-wrap gap-2">{actionButtonElements}</div>
        )}
      </AbilityContainer>
    );
  }

  if (meta.containerContext === 'domain-card') {
    const cardData = {
      name: meta.name,
      domain: meta.domain || 'Unknown',
      level: meta.level || '?',
      cardType: meta.cardType || 'Ability',
      text: meta.description || '',
    };
    return (
      <DomainCardContainer
        cardKey={cardKey}
        card={cardData}
        cardType={meta.cardType}
        description={meta.summary || `${meta.domain || 'Domain'} card`}
        fullDescriptionOverride={meta.description}
      >
        <div className="flex flex-wrap gap-2 items-center">
          {actionButtonElements}
          {tokens?.length > 0 && renderTokenDisplays('number')}
        </div>
        {renderActiveIndicator()}
        {renderInputCollector()}
      </DomainCardContainer>
    );
  }

  // Fallback: render without container (e.g., Workshop preview)
  return (
    <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
      <h4 className="text-sm font-semibold text-white mb-2">{meta.name}</h4>
      {meta.description && <p className="text-xs text-gray-400 mb-2">{meta.description}</p>}
      {renderTokenDisplays('dots')}
      <div className="flex flex-wrap gap-2">{actionButtonElements}</div>
      {renderActiveIndicator()}
      {renderInputCollector()}
    </div>
  );
};

export default ComposedMechanicRenderer;
